import { useMemo, useRef, useState } from "react";
import useNewActiveController from "../../hook/useActiveController";

import styles from "./styles.module.css";
import { randomID } from "../../utils";

import { ReactComponent as ErrorIcon } from "../../assets/icons/error.svg";
import { ReactComponent as VisibleIcon } from "../../assets/icons/visible.svg";
import { ReactComponent as InvisibleIcon } from "../../assets/icons/invisible.svg";

export default function Input({
  disabled = false,
  style = {},
  className = "",
  children = null,
  icon = null,
  action = null,
  errored = false,
  type = "text",
  visibility = false,
  id,
  ...props
}: React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & {
  icon?: React.ReactNode;
  action?: React.ReactNode;
  errored?: boolean;
  visibility?: boolean;
}) {
  const _id = useMemo(
    () => (typeof id === "string" && id !== "" ? id : randomID()),
    [id]
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const [visible, setVisible] = useState(false);
  return (
    <div
      className={`${styles.root} ${disabled && styles.disabled} ${
        errored && styles.errored
      } ${className}`}
      style={style}
      onClick={(e) => {
        if (!e.target || !inputRef.current || disabled) return;
        if (e.target === inputRef.current) return;
        if ((e.target as HTMLDivElement).classList.contains(styles.root))
          inputRef.current.focus();
      }}
    >
      {icon !== null && (
        <label className={styles.icon} htmlFor={_id}>
          {icon}
        </label>
      )}
      <input
        id={_id}
        disabled={disabled}
        ref={inputRef}
        type={!visibility ? type : visible ? "text" : type}
        {...props}
      />
      {children}
      {action}
      {visibility && (
        <VisibilityButton
          visible={visible}
          onChange={setVisible}
          disabled={disabled}
        />
      )}
    </div>
  );
}

export const actionButtonClassName = styles.actionButton;

export function InputError({
  active = false,
  children = "",
  className = "",
  ...props
}) {
  const controller = useNewActiveController(active, 200, false);

  return !controller.view ? null : (
    <div className={`${styles.error} ${controller.animate && styles.active}`}>
      <ErrorIcon />
      <span>{children}</span>
    </div>
  );
}

function VisibilityButton({
  visible = false,
  onChange = (visible: boolean) => null,
  disabled = false,
}: {
  visible: boolean;
  onChange: (visible: boolean) => void;
  disabled?: boolean;
}) {
  const visibleController = useNewActiveController(visible, 200, false);
  const invisibleController = useNewActiveController(!visible, 200, false);

  return (
    <button
      className={styles.visibility}
      disabled={disabled}
      onClick={() => onChange(!visible)}
      type="button"
    >
      {visibleController.view && (
        <InvisibleIcon
          className={`${visibleController.animate && styles.active}`}
        />
      )}
      {invisibleController.view && (
        <VisibleIcon
          className={`${invisibleController.animate && styles.active}`}
        />
      )}
    </button>
  );
}
